/* eslint-disable @typescript-eslint/no-unused-vars */
import * as Yup from "yup";
import React, { useCallback, useEffect, useState } from "react";
import axios, { Method } from "axios";
import { AiOutlineLock } from "react-icons/ai";
import { IoMdArrowForward } from "react-icons/io";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { useMediaQuery } from "react-responsive";

import ContainedButton from "components/layout/form/containedButton";
import CustomToolip from "./CustomToolip";
import PasswordForceMobile from "./PasswordForceMobile";
import TextField from "components/layout/form/textField";
import api from "services/api";
import { passwordRegex } from "utils/regex";
import { toggleStep } from "store/actions/first-access-mi-actions";

import {
  Container,
  ContainerButtons,
  Form,
  HeaderContainer,
  SearchDocumentIcon,
  SearchDocumentText,
  WrapSearchDocument,
} from "./styles";

const RegisterPassword = (): JSX.Element => {
  const [enviado, setEnviado] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [invalidToken, setInvalidToken] = useState(false);
  const [showPasswordForce, setShowPasswordForce] = useState(false);

  const { location, contactId } = useSelector(
    // @ts-ignore
    (state) => state.firstAccessReducer
  );
  const dispatch = useDispatch();
  const showMobileMode = useMediaQuery({ maxWidth: 959 });

  const validationSchema = (): any =>
    Yup.object().shape({
      password: Yup.string()
        .required("Insira uma senha")
        .matches(
          passwordRegex,
          "Use o mínimo de 8 caracteres, com uma combinação de letras maiúsculas, minúsculas, números e símbolos."
        ),
      passwordConfirmation: Yup.string()
        .required("Insira a confirmação de sua senha")
        .oneOf([Yup.ref("password"), null], "As senhas não combinam"),
    });

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordConfirmation: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await requestRegisterPassword(values);
    },
  });

  const requestRegisterPassword = async ({
    password,
    passwordConfirmation,
  }): Promise<void> => {
    const headers = {
      access_token: accessToken,
    };
    const body = {
      contactId,
      password,
      passwordConfirmation,
    };

    try {
      const response = await api.post(`${location}/passwords`, body, {
        headers,
      });

      const tokenMFA = response.headers["x-mfa-hash"];

      if (tokenMFA) {
        localStorage.setItem("@UltragazEmpresarial:X-Mfa-Hash", tokenMFA);
      }

      dispatch(toggleStep(4));
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;
        if (err.response.status === 422) {
          setInvalidToken(true);
        }
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  useEffect(() => {
    (async () => {
      await generateAccessToken();
    })();
  }, []);

  const generateAccessToken = async (): Promise<void> => {
    const req = axios.create({
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": "pt-BR",
        client_id: process.env.REACT_APP_CLIENT_ID,
      },
      baseURL: process.env.REACT_APP_API_URL_OAUTH,
    });

    const header = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST" as Method,
      headers: header,
    };
    const body = {
      grant_type: "client_credentials",
    };

    try {
      const result = await req.post("oauth/access-token", body, parameters);

      setAccessToken(result.data.access_token);
    } catch (err) {
      const message =
        err.response.data[0]?.message || err.response.data.message;

      toast.error(message);
    }
  };

  const handleShowPasswordForce = useCallback(() => {
    setShowPasswordForce((prev) => !prev);
  }, [showPasswordForce]);

  return (
    <Container>
      <HeaderContainer>
        <h1>Estamos quase lá!</h1>
        <h2>Agora vamos criar uma senha</h2>
      </HeaderContainer>
      <Form onSubmit={formik.handleSubmit}>
        <TextField
          id="password"
          name="password"
          type="password"
          label="Senha"
          placeholder="Senha"
          aria-label="Senha"
          aria-required
          errorCondition={!!formik.errors.password && !!formik.touched.password}
          errorMessage={formik.errors.password}
          showChangeTextPassword
          onBlur={formik.handleBlur}
          value={formik.values.password}
          onChange={formik.handleChange}
          LeadingIcon={AiOutlineLock}
          propsLeadingIcon={{
            size: 20,
          }}
        />

        <WrapSearchDocument>
          {!showMobileMode && (
            <CustomToolip title="Requisitos de Senha">
              <SearchDocumentText>Requisitos de senha</SearchDocumentText>
              <SearchDocumentIcon>!</SearchDocumentIcon>
            </CustomToolip>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {showMobileMode && (
              <div
                style={{
                  display: "flex",
                }}
                onClick={handleShowPasswordForce}
              >
                <SearchDocumentText>Requisitos de senha</SearchDocumentText>
                <SearchDocumentIcon>!</SearchDocumentIcon>
              </div>
            )}

            {showMobileMode && showPasswordForce && <PasswordForceMobile />}
          </div>
        </WrapSearchDocument>

        <TextField
          id="passwordConfirmation"
          name="passwordConfirmation"
          type="password"
          label="Confirmação de Senha"
          placeholder="Confirmação de Senha"
          aria-label="Confirmação de Senha"
          aria-required
          errorCondition={
            !!formik.errors.passwordConfirmation &&
            !!formik.touched.passwordConfirmation
          }
          errorMessage={formik.errors.passwordConfirmation}
          showChangeTextPassword
          onBlur={formik.handleBlur}
          value={formik.values.passwordConfirmation}
          onChange={formik.handleChange}
          LeadingIcon={AiOutlineLock}
          propsLeadingIcon={{
            size: 20,
          }}
        />

        <ContainerButtons>
          <ContainedButton
            type="submit"
            loading={formik.isSubmitting}
            id="Avançar"
            width="100%"
            TrailingIcon={IoMdArrowForward}
            propsTrailingIcon={{
              size: 20,
            }}
          >
            Continuar
          </ContainedButton>
        </ContainerButtons>
      </Form>
    </Container>
  );
};

export default RegisterPassword;
